import React, { /* useCallback , */ useEffect, useState } from "react";

import {
  DocumentList,
  DocumentsModal,
  InfoPanel,
  Typography,
  Checkbox,
  Button,
  IconWrapper,
  DocumentListItem,
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as Download } from "@king-ict/eupisi-ui/components/Icon/Download.svg";
import { UploadFile } from "@king-ict/eupisi-ui/models";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import {
  getRegistrationFormByRequestId,
  getRequestDocument,
  createDigitalConfirmationForm,
} from "../../../api/Request/RequestApi";
import { RootState } from "../../../app/rootReducer";
import { DocumentType } from "../../../models/Request/DocumentType";
import { RegistrationFormStatus } from "../../../models/Request/RegistrationFormStatus";
import { RegistrationFormStatusEnum } from "../../../models/Request/RegistrationFormStatusEnum";
import RequestStatus from "../../../models/Request/RequestStatusEnum";
import { Attachment } from "../../../models/Shared/Attachment";
import { RequestFormCard, RequestFormTopMessage } from "../RequestComponents";
import { ManualSigningContent } from "./ManualSigningContent";
import { ToggleSwitch } from "./ToggleSwitch";
import { downloadPdfFromBase64 } from "../../../utils";

interface Props {
  canUpload: (val: boolean) => void;
  filesToUploadParent: (files: UploadFile[]) => void;
}

export interface SignedAttachmentResponseModel {
  id: number;
  fileID: string;
  fileName: string;
  fileExtension: string;
  fileSizeKB: number;
}

export default function EnrollmentStep({
  canUpload,
  filesToUploadParent,
}: Props) {
  const { t } = useTranslation();
  const request = useSelector((state: RootState) => state.request.request);

  const [showUploadModal, setShowUploadModal] = useState<DocumentType>();
  const [digitalConfirmedForm, setDigitalConfirmedForm] =
    useState<Attachment | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>();
  const [filesToUpload, setFilesToUpload] = useState<UploadFile[]>();
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const file = digitalConfirmedForm?.file;
  const [registrationFormState, setRegistrationFormState] = useState<{
    isRegistrationFormDownloaded: boolean;
    isDigitallyConfirmed: boolean;
    registrationFormStatus: RegistrationFormStatus | null;
  }>({
    isRegistrationFormDownloaded: false,
    isDigitallyConfirmed: false,
    registrationFormStatus: null,
  });
  const passToParentCanUpload = (val: any) => {
    canUpload(val);
  };

  const passToParentFilesToUpload = (val: any) => {
    filesToUploadParent(val!);
  };
  const signedAttachmentsCheck = async () => {
    await getRegistrationFormByRequestId(request.id!).then((response) => {
      if (response) {
        setRegistrationFormState({
          isRegistrationFormDownloaded: response.registrationFormDownloaded,
          isDigitallyConfirmed: response.digitalConfirmation,
          registrationFormStatus: response.status,
        });
        if (
          response.signedAttachments !== null &&
          response.signedAttachments !== undefined
        ) {
          if (response.signedAttachments.length > 0) {
            setAttachments(response.signedAttachments);
          }
        }
      }
    });
  };

  const signDigitalConfirmationForm = async () => {
    const digitalConfirmationRaw: Attachment =
      await createDigitalConfirmationForm(request.id!);
    setDigitalConfirmedForm(digitalConfirmationRaw);

    if (digitalConfirmationRaw.file) {
      const contentType = "data:application/pdf;base64";
      const base64Data = `${contentType},${digitalConfirmationRaw.file}`;

      const uploadFile: UploadFile = {
        fileName: digitalConfirmationRaw.fileName,
        fileData: base64Data,
        digitalConfirmation: true,
      };

      setFilesToUpload((prev) => {
        const updatedFiles = prev ? [...prev, uploadFile] : [uploadFile];
        passToParentFilesToUpload(updatedFiles);
        passToParentCanUpload(true);
        return updatedFiles;
      });
    }

    await signedAttachmentsCheck();
  };

  useEffect(() => {
    signedAttachmentsCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEnrollmentRegistrationDownload: () => Promise<void> =
    async () => {
      const response = await getRegistrationFormByRequestId(request.id!);
      const registrationEnrollmentDocumentId = response?.attachment?.id;

      if (
        registrationEnrollmentDocumentId > 0 &&
        typeof registrationEnrollmentDocumentId === "number"
      ) {
        await getRequestDocument(
          registrationEnrollmentDocumentId,
          request.id!,
          DocumentType.RegistrationForm
        );
        signedAttachmentsCheck();
      }
    };

  const canUploadRegistrationForm =
    request.statusId === RequestStatus.RegistrationFormCreated ||
    request.statusId === RequestStatus.RegistrationSigned;

  const handleFileChange = async (files: File[]) => {
    let uploadedFiles: UploadFile[] = [];
    if (files && files.length > 0) {
      files.forEach((file) => {
        const fileReader = new FileReader();
        fileReader.readAsBinaryString(file);
        var uploadedFile = {
          fileName: file.name,
          fileData: fileReader.result,
        } as UploadFile;
        uploadedFiles.push(uploadedFile);
      });
    }
  };

  const handleUploadFileChange = async (files: UploadFile[]) => {
    switch (showUploadModal) {
      case DocumentType.RegistrationForm:
        setShowUploadModal(undefined);

        let _tempFilesToUpload: UploadFile[] = [];
        files.forEach((file) => {
          _tempFilesToUpload.push(file);
        });

        filesToUpload?.forEach((x) => {
          _tempFilesToUpload.push(x);
        });

        if (_tempFilesToUpload) {
          if (_tempFilesToUpload.length > 0) {
            passToParentCanUpload(true);
          }
        }

        passToParentFilesToUpload(_tempFilesToUpload);
        setFilesToUpload(_tempFilesToUpload);

        break;
      default:
        break;
    }
  };

  const handleRemoveTempUploadFile = (file: UploadFile) => {
    if (filesToUpload) {
      let fileIndex = filesToUpload?.findIndex(
        (x) => x.fileName.toLowerCase() === file.fileName.toLowerCase()
      );

      if (fileIndex > -1) {
        let newList = filesToUpload.filter((x) => x.fileName !== file.fileName);
        if (newList) {
          if (newList.length > 0) {
            passToParentCanUpload(true);
          } else {
            passToParentCanUpload(false);
          }
        }
        setFilesToUpload(newList);
        passToParentFilesToUpload(newList);
      }
    }
  };

  const handleUploadDocumentClick = (documentType: DocumentType) => {
    setShowUploadModal(documentType);
  };

  const handleDocumentDownload = async (
    doc: Attachment,
    type: DocumentType
  ) => {
    if (doc && doc.id) {
      await getRequestDocument(doc.id, request.id!, type);
    }
  };

  const isManualSigningContentVisible =
    registrationFormState.registrationFormStatus?.id != null &&
    registrationFormState.registrationFormStatus.id !==
      RegistrationFormStatusEnum.FirstSignatureInProgress &&
    request.statusId === RequestStatus.RegistrationFormCreated;

  return (
    <>
      {request.statusId === RequestStatus.Registered && (
        <Row className="mt-3">
          <Col md={6}>
            <Typography variant="baseBold" className="border-bottom">
              {t("enrollmentStep.ConfirmedDocument")}
            </Typography>
            <DocumentList
              documents={attachments ?? []}
              onDocumentClick={(doc) =>
                handleDocumentDownload(doc, DocumentType.RegistrationForm)
              }
              onRemoveClick={(doc) => console.log(doc)}
              removable={false}
            />
          </Col>
        </Row>
      )}
      {request.statusId !== RequestStatus.Registered && (
        <RequestFormTopMessage
          title={t("enrollmentStep.AdditionalInformation")}
          infoMessage={t("enrollmentStep.SigningInstructions")}
          color="info"
        />
      )}

      {request.statusId !== RequestStatus.Registered && (
        <RequestFormTopMessage
          title={t("enrollmentStep.mainInfoTitle")}
          infoMessage={t("enrollmentStep.mainInfoMessage")}
        />
      )}
      {request.statusId !== RequestStatus.Registered && (
        <RequestFormCard
          cardTitle={t("enrollmentStep.mainInfoTitle")}
          cardSubTitle={""}
          refreshTitle={t("common.download")}
          showRefresh
          refreshIcon={
            <IconWrapper color="primary">
              <Download />
            </IconWrapper>
          }
          onClickRefresh={handleEnrollmentRegistrationDownload}
        />
      )}
      {RegistrationFormStatusEnum.FirstSignatureInProgress ===
        registrationFormState.registrationFormStatus?.id && (
        <>
          <RequestFormCard
            cardTitle={t("enrollmentStep.ConfirmationOfEnrollment")}
            cardSubTitle={""}
          >
            <InfoPanel
              message={t("enrollmentStep.ConfirmEnrollmentMessage")}
              color="warning"
            />

            <Row className="mt-3">
              <Col>
                <Checkbox
                  id="confirmEnrollment"
                  label={t("enrollmentStep.ApplicantParentConfirmation")}
                  disabled={
                    !registrationFormState.isRegistrationFormDownloaded ||
                    registrationFormState.isDigitallyConfirmed ||
                    digitalConfirmedForm !== null
                  }
                  checked={isConfirmed}
                  onChange={(e) => setIsConfirmed(e.target.checked)}
                />
              </Col>
            </Row>

            <Row className="mt-3">
              <Col className="text-left">
                <Button
                  outline
                  size="sm"
                  disabled={
                    !isConfirmed ||
                    registrationFormState.isDigitallyConfirmed ||
                    digitalConfirmedForm !== null
                  }
                  onClick={signDigitalConfirmationForm}
                >
                  {t("enrollmentStep.DigitalConfirmationForm")}
                </Button>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col md={6}>
                <Typography variant="baseBold" className="border-bottom">
                  {t("enrollmentStep.ConfirmedDocument")}
                </Typography>
                {digitalConfirmedForm && typeof file === "string" && (
                  <DocumentListItem
                    document={digitalConfirmedForm}
                    onDocumentClick={() =>
                      downloadPdfFromBase64(
                        file,
                        digitalConfirmedForm?.fileName
                      )
                    }
                    removable={false}
                    color="base"
                  />
                )}
                {!digitalConfirmedForm && !!attachments?.length && (
                  <DocumentList
                    documents={
                      attachments?.filter((doc) =>
                        doc.fileName.includes("DigitalnoPotvrdenaUpisnica")
                      ) ?? []
                    }
                    onDocumentClick={(doc) =>
                      handleDocumentDownload(doc, DocumentType.RegistrationForm)
                    }
                    removable={false}
                  />
                )}
              </Col>
            </Row>
          </RequestFormCard>
          <RequestFormCard>
            <Row className="mb-3">
              <Col>
                <ToggleSwitch
                  isExpanded={isExpanded}
                  onToggle={() => setIsExpanded(!isExpanded)}
                />
              </Col>
            </Row>

            {isExpanded && (
              <>
                <InfoPanel
                  message={t("enrollmentStep.ManualSigningInstructions")}
                  color="warning"
                />
                <ManualSigningContent
                  canUploadRegistrationForm={canUploadRegistrationForm}
                  handleUploadDocumentClick={handleUploadDocumentClick}
                  attachments={attachments}
                  handleDocumentDownload={handleDocumentDownload}
                  filesToUpload={filesToUpload}
                  handleRemoveTempUploadFile={handleRemoveTempUploadFile}
                  requestStatusId={request.statusId}
                />
              </>
            )}
          </RequestFormCard>
        </>
      )}
      {isManualSigningContentVisible && (
        <ManualSigningContent
          canUploadRegistrationForm={canUploadRegistrationForm}
          handleUploadDocumentClick={handleUploadDocumentClick}
          attachments={attachments}
          handleDocumentDownload={handleDocumentDownload}
          filesToUpload={filesToUpload}
          handleRemoveTempUploadFile={handleRemoveTempUploadFile}
          requestStatusId={request.statusId}
        />
      )}
      <DocumentsModal
        title={t("documentUpload.documentModal.title")}
        infoPanelText={t("documentUpload.documentModal.info")}
        types={t("documentUpload.documentModal.types")}
        allDocuments={[]}
        maxSize={10}
        info={""}
        filesAction={handleFileChange}
        open={showUploadModal !== undefined}
        onClose={() => setShowUploadModal(undefined)}
        onConfirm={handleUploadFileChange}
        hideOldDocuments={true}
        btnCancelText={t("common.quit")}
        btnOkText={t("common.addNew")}
      />
    </>
  );
}
